import { useRouter } from 'next/navigation';
import { VideoPlayer } from 'src/components/VideoPlayer';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useLayoutType } from 'src/hooks/useLayoutType';
import {
  ArticleContent,
  ArticleContentBlockTypes,
} from 'src/types/articleCmsTypes';

import {
  ArticleBanner,
  ArticleTable,
  Code,
  CTAButton,
  Highlight,
  ImageArticle,
  Quote,
  TextBlock,
} from '../../helperComponents';

type BlogContentProps = ArticleContent;

export function BlogContent(props: BlogContentProps) {
  const { blocks } = props;

  const router = useRouter();

  const { clickAnalytics } = useAnalytics();

  const layoutType = useLayoutType();

  const handleCTAButtonClick = (link: string) => {
    if (link.startsWith('https://')) {
      clickAnalytics({
        action: link,
        clickContent: 'CTA Кнопка',
        clickElement: 'button',
        clickZone: 'body',
        clickZoneCategory: 'site-body',
        transitionType: 'outside-link',
        uniqueId: `article-cta-button-${link}`,
        transitionTypeMetrics: 'external',
        transitionUrl: link,
      });

      globalThis.window.open(link);
    } else {
      const normalizedLink = link.startsWith('/') ? link : `/${link}`;

      clickAnalytics({
        action: normalizedLink,
        clickContent: 'CTA Кнопка',
        clickElement: 'button',
        clickZone: 'body',
        clickZoneCategory: 'site-body',
        transitionType: 'inside-link',
        uniqueId: `article-cta-button-${normalizedLink}`,
        transitionTypeMetrics: 'internal',
        transitionUrl: normalizedLink,
      });

      router.push(normalizedLink);
    }
  };

  return blocks.map((block, index) => {
    const key = `${block.type}-${index}`;

    switch (block.type) {
      case ArticleContentBlockTypes.Image:
        if (!block.src) {
          return null;
        }

        return (
          <ImageArticle
            key={key}
            image={{ src: block.src, alt: block.alt }}
            description={block.description}
          />
        );
      case ArticleContentBlockTypes.Quote:
        if (!block.text) {
          return null;
        }

        return (
          <Quote
            key={key}
            layoutType={layoutType}
            text={block.text}
            name={block.author}
            position={block.post}
          />
        );
      case ArticleContentBlockTypes.Highlight:
        if (!block.richText) {
          return null;
        }

        return (
          <Highlight
            key={key}
            layoutType={layoutType}
            richText={block.richText}
          />
        );
      case ArticleContentBlockTypes.RawHTML:
        if (!block.html) {
          return null;
        }

        return (
          <div key={key} dangerouslySetInnerHTML={{ __html: block.html }} />
        );
      case ArticleContentBlockTypes.TextBlock:
        return (
          <TextBlock
            key={key}
            layoutType={layoutType}
            id={block.id}
            title={block.title}
            richText={block.richText}
          />
        );
      case ArticleContentBlockTypes.Video:
        if (!block.link) {
          return null;
        }

        return (
          <VideoPlayer
            key={key}
            controls
            link={block.link}
            previewImage={block.previewImage}
          />
        );
      case ArticleContentBlockTypes.Code:
        if (!block.code) {
          return null;
        }

        return <Code key={key} code={block.code} />;
      case ArticleContentBlockTypes.CTAButton:
        if (!block.title || !block.link) {
          return null;
        }

        return (
          <CTAButton
            key={key}
            title={block.title}
            link={block.link}
            onClick={handleCTAButtonClick}
          />
        );
      case ArticleContentBlockTypes.ArticleBanner:
        return <ArticleBanner key={key} />;
      case ArticleContentBlockTypes.Table:
        if (!block.rows || !block.rows.length) {
          return null;
        }

        return <ArticleTable key={key} rows={block.rows} />;
    }
  });
}
